/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useContext } from "react";
import {
  Grid,
  Container,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import DashboardItem from "../../components/DashboardItem";
import Header, { HeaderType } from "../../components/Header";
import {
  GetProducts,
  sendPanicRequest,
} from "../../services/api/product-service";
import { GetClient } from "../../services/api/client-service";
import type { IProduct } from "../../services/model/product";
import type { PanicRequest } from "../../services/model/panicRequest";
import type { IClient } from "../../services/model/client";
import { useAuth, getAccessToken } from "../../context/auth";
import { useNavigate, useParams } from "react-router-dom";
import usePosition from "../../hooks/usePosition";
import "./styles.css";
import { useConfirm } from "@/components/dialog/confirm";
import WhatsAppFloatButton from "@/components/WhatsAppFloatButton";
import BrandLoader from "@/branding/BrandLoader";
import PanicTypeOverlayFloating from "./PanicTypeOverlayFloating";
import { BrandingContext } from "@/branding/BrandingProvider";

// 🔸 lightweight cache helpers (see "@/utils/cache")
import { loadCache, saveCache, cacheKey } from "@/utils/cache";

interface DashboardProps {
  style?: any;
  loading: boolean;
}

const Dashboard: React.FC<DashboardProps> = () => {
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const [products, setProducts] = useState<IProduct[]>([]);
  const [client, setClient] = useState<IClient>();
  const { latitude, longitude } = usePosition({ enableHighAccuracy: true });
  const confirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const [allowedMask, setAllowedMask] = useState<number | null>(null);
  const branding = useContext(BrandingContext);
  const [canShowWhatsAppButton, setCanShowWhatsAppButton] = useState(true);

  // ---------- Online/Offline state ----------
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // ---------- Tenant/Brand version for cache keys ----------
  const tenantId = tenant || "";
  const brandVersion = branding?.version || "";

  // ---------- Load cached data immediately (so offline renders) ----------
  useEffect(() => {
    const kProducts = cacheKey(tenantId, "products", brandVersion);
    const kClient = cacheKey(tenantId, "client", brandVersion);
    const cachedProducts = loadCache<IProduct[]>(kProducts);
    const cachedClient = loadCache<IClient>(kClient);
    if (cachedProducts) setProducts(cachedProducts);
    if (cachedClient) setClient(cachedClient);
  }, [tenantId, brandVersion]);

  // ---------- Fetch fresh data when online, then cache ----------
  useEffect(() => {
    if (!branding || !token || !isOnline) return;

    (async () => {
      setLoading(true);
      try {
        const [prodRes, clientRes] = await Promise.all([
          GetProducts(token, branding.clientId),
          GetClient(token, branding.clientId),
        ]);

        const prod = (prodRes?.data ?? []) as IProduct[];
        const cli = clientRes?.data as IClient | undefined;

        setProducts(prod);
        if (cli) setClient(cli);

        // Cache for 7 days; versioned by branding.version + tenant
        const ttl = 7 * 24 * 60 * 60 * 1000;
        saveCache(cacheKey(tenantId, "products", brandVersion), prod, ttl);
        if (cli)
          saveCache(cacheKey(tenantId, "client", brandVersion), cli, ttl);

        // Allowed panic mask from client (if needed)
        setAllowedMask(
          typeof cli?.panicTypes === "number" ? cli!.panicTypes : null
        );
      } catch (e) {
        console.error(e);
        // keep cached data if any
      } finally {
        setLoading(false);
      }
    })();
  }, [branding, token, isOnline, tenantId, brandVersion]);

  if (!branding) {
    return <BrandLoader open />; // small skeleton/loader
  }

  // ---------- NAVIGATION (tenant-aware) ----------
  const go = (path: string) => {
    if (tenant) navigate(`/${tenant}/${path}`);
  };
  const routeTomyCover = () => go("mycover");
  const routeToadditionalServices = () => go("additionalservices");

  // ---------- Panic flow (online only) ----------
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [pendingProduct, setPendingProduct] = useState<IProduct | null>(null);

  const openTypePicker = (product: IProduct) => {
    setCanShowWhatsAppButton(false);
    setPendingProduct(product);
    setTypeDialogOpen(true);
  };

  const submitPanic = async (panicTypeValue: number) => {
    try {
      setLoading(true);
      setCanShowWhatsAppButton(true);

      const panic: PanicRequest = {
        clientId: branding?.clientId,
        latitude: latitude != null ? String(latitude) : "",
        longitude: longitude != null ? String(longitude) : "",
        PanicTypes: panicTypeValue,
      };

      const response = await sendPanicRequest(panic, token);
      setLoading(false);

      if (response?.resultCode) {
        showDialog("Panic Alert Successful", response.message);
      } else {
        if (pendingProduct)
          productConfirm(
            pendingProduct,
            `Your request has been submitted, in the meanwhile do you wish to contact: ${
              pendingProduct.clickToDial || ""
            }`
          );
        else
          showDialog(
            "Panic Alert Failed",
            response?.message ?? "Unknown error"
          );
      }
    } catch (err) {
      setLoading(false);
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator."
      );
      if (err instanceof Error) console.error(err.message);
    }
  };

  const handleTypePicked = (panicTypeValue: number) => {
    submitPanic(panicTypeValue);
  };

  // ---------- Offline-safe Click-to-Dial confirm (real anchor) ----------
  const [dialOpen, setDialOpen] = useState(false);
  const [dialTitle, setDialTitle] = useState<string>("");
  const [dialHref, setDialHref] = useState<string>("");
  const [dialDesc, setDialDesc] = useState<string>("");

  const productConfirm = (product: IProduct, description?: string) => {
    const isLink = !!product.clickToDialIsLink;
    const href = isLink
      ? String(product.clickToDial || "")
      : `tel:${String(product.clickToDial || "").replace(/\s+/g, "")}`;

    setDialTitle(product.name || (isLink ? "Open link" : "Call"));
    setDialDesc(
      description ??
        (isLink
          ? `Are you sure you would like to go to ${product.name}?`
          : `Are you sure you would like to call ${product.name}?`)
    );
    setDialHref(href);
    setDialOpen(true);
  };

  const showDialog = (title: string, message: string) => {
    confirm({ catchOnCancel: false, title, description: message });
  };

  return (
    <div>
      <Header type={HeaderType.Dashboard} />

      <Container maxWidth="sm">
        {!isOnline && (
          <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
            Offline mode: you can still tap a number to <strong>call</strong>.
            Panic, Forms and Brochure are hidden while offline.
          </Alert>
        )}

        <Grid container spacing={1}>
          {/* Panic buttons: HIDE when offline */}
          {branding?.enablePanicButton && isOnline && (
            <Grid size={12}>
              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="stretch"
                spacing={1}
              >
                {products.map(
                  (product, index) =>
                    product.isPanic === true && (
                      <Grid key={"panic_" + index} size={6}>
                        <br />
                        <DashboardItem
                          style={{ color: "white" }}
                          loading={false}
                          productData={product}
                          buttonClickEvent={() => {
                            openTypePicker(product);
                          }}
                        />
                        <br />
                      </Grid>
                    )
                )}
              </Grid>
            </Grid>
          )}

          {/* Click to Dial (always available) */}
          <Grid size={12}>
            <Grid container direction="column" spacing={1}>
              <Grid size={12}>
                <Grid container justifyContent="center">
                  <Typography color="primary" variant="h5">
                    Click to Dial
                  </Typography>
                </Grid>
                <hr />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={12}>
            <Grid container direction="row" justifyContent="center" spacing={1}>
              {products.map(
                (product, index) =>
                  product.isPanic === false && (
                    <Grid key={"callbtn_" + index} size={4}>
                      <DashboardItem
                        loading={false}
                        productData={product}
                        buttonClickEvent={() => {
                          productConfirm(product);
                        }}
                      />
                    </Grid>
                  )
              )}
              <Grid size={12}>
                <br />
              </Grid>
            </Grid>
          </Grid>

          {/* Additional Services: HIDE when offline */}
          {(branding?.enableBrochure || branding?.enableForms) && isOnline && (
            <>
              <Grid size={12}>
                <Grid container justifyContent="center">
                  <Typography color="primary" variant="h5">
                    Additional Services
                  </Typography>
                </Grid>
                <hr />
              </Grid>

              <Grid size={12}>
                <Grid container justifyContent="center" spacing={2}>
                  {branding?.enableBrochure && (
                    <Grid size={6}>
                      <Card
                        variant="outlined"
                        onClick={routeTomyCover}
                        sx={{ textAlign: "center", cursor: "pointer" }}
                      >
                        <CardContent
                          sx={{
                            py: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <DescriptionIcon
                            color="secondary"
                            sx={{ fontSize: 48 }}
                          />
                        </CardContent>
                        <CardActions sx={{ p: 0 }}>
                          <Button
                            fullWidth
                            color="primary"
                            sx={{
                              fontSize: "12px",
                              minHeight: 44,
                              fontWeight: 700,
                            }}
                          >
                            BROCHURE
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  )}

                  {branding?.enableForms && (
                    <Grid size={6}>
                      <Card
                        variant="outlined"
                        onClick={routeToadditionalServices}
                        sx={{ textAlign: "center", cursor: "pointer" }}
                      >
                        <CardContent
                          sx={{
                            py: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <DescriptionIcon
                            color="secondary"
                            sx={{ fontSize: 48 }}
                          />
                        </CardContent>
                        <CardActions sx={{ p: 0 }}>
                          <Button
                            fullWidth
                            color="primary"
                            sx={{
                              fontSize: "12px",
                              minHeight: 44,
                              fontWeight: 700,
                            }}
                          >
                            FORMS
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Container>

      {/* Panic types: only when online */}
      {isOnline && (
        <PanicTypeOverlayFloating
          open={typeDialogOpen}
          onClose={() => setTypeDialogOpen(false)}
          onSend={(panicType) => handleTypePicked(panicType)}
          allowedMask={allowedMask ?? undefined}
          confirmTitle={pendingProduct?.name ?? "Assistance"}
        />
      )}

      {/* WhatsApp stays available online/offline (device decides how to handle) */}
      {canShowWhatsAppButton && branding?.enableWhatsApp && (
        <WhatsAppFloatButton client={client} />
      )}

      <BrandLoader open={loading} />

      {/* Offline-safe Click-to-Dial confirm dialog (real anchor) */}
      <Dialog open={dialOpen} onClose={() => setDialOpen(false)}>
        <DialogTitle>{dialTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialDesc}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDialOpen(false)}>Cancel</Button>
          {/* critical: Confirm is a real anchor, so iOS/Android allow it offline */}
          <Button
            component="a"
            href={dialHref}
            onClick={() => setDialOpen(false)}
            variant="contained"
          >
            {dialHref.startsWith("tel:") ? "Call" : "Go To"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Dashboard;
